import * as React from "react"
import { useContext, useEffect, useState } from "react"

import styles from "./TableOfContents.module.scss"

export interface ITOCContext {
    readonly toc: TOCNode[]
    readonly push: (item: TOCItem) => void
}

export const TOCContext = React.createContext<ITOCContext>({
    toc: [],
    push: () => {
    }
})

export function TableOfContents(props: {}) {
    const context = useContext<ITOCContext>(TOCContext)

    return (
        <div className={styles.TableOfContents}>
            <p className={styles.Heading}>Content</p>
            <ul>
                {context.toc.map(renderTOCNode)}
            </ul>
        </div>
    )
}

function renderTOCNode(node: TOCNode, index: number) {
    const hasChildren = node.children.length > 0
    return (
        <li key={index}>
            <a href={`#${node.id}`}>{node.text}</a>
            { hasChildren && (
                <ul>
                    {node.children.map(renderTOCNode)}
                </ul>
            ) }
        </li>
    )
}

interface TOCItem {
    id: string
    text: string
    level: number
}

interface TOCNode {
    id: string
    text: string
    level: number
    children: TOCNode[]
    parent: TOCNode | null
}

class TOCData {
    private onUpdate: () => void = () => {
    }

    constructor() {
    }

    private _toc: TOCNode[] = []
    private _currentNode: TOCNode | null = null

    get toc(): TOCNode[] {
        return [...this._toc]
    }

    push = (newItem: TOCItem) => {
        if (this._currentNode && newItem.level > this._currentNode.level) {
            const newNode = {
                ...newItem,
                children: [],
                parent: this._currentNode
            }
            this._currentNode.children.push(newNode)
            this._currentNode = newNode
        } else if (this._currentNode?.parent && newItem.level === this._currentNode.level) {
            const newNode = {
                ...newItem,
                children: [],
                parent: this._currentNode?.parent
            }
            this._currentNode.parent.children.push(newNode)
            this._currentNode = newNode
        } else if (this._currentNode && newItem.level < this._currentNode.level) {
            const levelDiff = this._currentNode.level - newItem.level
            const parent = findNodeToInsertTo(this._currentNode, levelDiff)

            const newNode = {
                ...newItem,
                children: [],
                parent: parent
            }

            if (parent) {
                parent.children.push(newNode)
            } else {
                this._toc.push(newNode)
            }

            this._currentNode = newNode
        } else {
            const newNode = {
                ...newItem,
                children: [],
                parent: null
            }
            this._toc.push(newNode)
            this._currentNode = newNode
        }

        this.onUpdate()
    }

    setOnUpdate = (onUpdate: () => void) => {
        this.onUpdate = onUpdate
        this.onUpdate()
    }
}

function findNodeToInsertTo(currentNode: TOCNode, levelDiff: number) {
    switch (levelDiff) {
        case 0:
            return currentNode?.parent ?? null
        case 1:
            return currentNode.parent?.parent ?? null
        case 2:
            return currentNode.parent?.parent?.parent ?? null
        case 3:
            return currentNode.parent?.parent?.parent?.parent ?? null
        case 4:
            return currentNode.parent?.parent?.parent?.parent?.parent ?? null
        case 5:
            return currentNode.parent?.parent?.parent?.parent?.parent?.parent ?? null
        default:
            return null
    }
}

export function WithTableOfContents(props: {
    children?: any
}) {
    const [toc, setToc] = useState<TOCNode[]>([])

    // Just using useCallback+setToc seems not to work as necessary.
    // Use a mutating object (TOCData) instead to store and get TOC updates
    const [tocData, _] = useState(new TOCData())

    useEffect(() => {
        tocData.setOnUpdate(() => {
            setToc(tocData.toc)
        })
    }, [tocData])

    const context = {
        toc: toc,
        push: tocData.push
    }

    return (
        <TOCContext.Provider value={context}>
            {props.children}
        </TOCContext.Provider>
    )
}